<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Skip to main content link (accessibility) -->
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    >
      Skip to main content
    </a>

    <!-- Configuration Loading State -->
    <div
      v-if="!appReady"
      role="status"
      aria-live="polite"
      aria-label="Loading application"
      class="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50"
    >
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" aria-hidden="true"></div>
        <p class="text-gray-600">Loading configuration...</p>
      </div>
    </div>

    <!-- Main App Content -->
    <div v-else>
      <!-- Sticky Header Container (includes banner + nav) -->
      <div class="sticky top-0 z-50 header-with-gradient">
        <!-- Promo Banner -->
        <PromoBanner />

        <!-- Header Component (uses themed override if available) -->
        <ThemedComponent name="AppHeader" />
      </div>

      <!-- Main Content -->
      <main
        id="main-content"
        class="flex-1 transition-all duration-300 relative"
        :style="mainContentStyle"
      >
        <router-view />
      </main>

      <!-- Login Modal -->
      <LoginModal v-if="showLoginModal" @close="closeLoginModal" />

      <!-- Toast Manager -->
      <ToastManager
        ref="toastManager"
        @view-cart="openCart"
        @continue-shopping="closeToasts"
      />

      <!-- Floating Cart Button (Mobile) -->
      <FloatingCartButton @open-cart="openCart" />

      <!-- Footer (uses themed override if available) -->
      <ThemedComponent name="AppFooter" />

      <!-- PWA Install Prompt (disabled for Windermere per CFG-06) -->
      <PwaInstallPrompt v-if="isPwaEnabled" />
    </div>
  </div>
</template>

<script>
import logger from '@/utils/logger'
import { mapFields } from 'vuex-map-fields'
import { mapGetters } from 'vuex'
import { updateApiCredentials } from '@/services/repository'
import ThemedComponent from '@/themes/veneer/ThemedComponent.vue'
import LoginModal from './components/auth/LoginModal.vue'
import ToastManager from './components/ui/ToastManager.vue'
import FloatingCartButton from './components/ui/FloatingCartButton.vue'
import PromoBanner from './components/ui/PromoBanner.vue'
import PwaInstallPrompt from './components/PwaInstallPrompt.vue'

export default {
  name: 'App',
  components: {
    ThemedComponent,
    LoginModal,
    ToastManager,
    FloatingCartButton,
    PromoBanner,
    PwaInstallPrompt
  },
  data() {
    return {
      appReady: false,
      isCartDocked: false
    }
  },
  computed: {
    ...mapFields([
      'showLoginModal'
    ]),
    ...mapGetters('config', [
      'configLoaded',
      'configLoading',
      'configError'
    ]),
    mainContentStyle() {
      // Adjust main content margin when cart is docked on desktop
      if (this.isCartDocked && window.innerWidth >= 768) {
        return {
          marginRight: '384px' // 24rem = w-96
        }
      }
      return {}
    },
    isPwaEnabled() {
      const school = this.getCurrentSchool()
      const pwaDisabledThemes = ['windermere', 'windermere_v2']
      return !pwaDisabledThemes.includes(school)
    }
  },
  watch: {
    configLoading(newVal, oldVal) {
      logger.debug('🔧 configLoading changed:', oldVal, '->', newVal);
    },
    configLoaded(newVal, oldVal) {
      logger.debug('🔧 configLoaded changed:', oldVal, '->', newVal);
    }
  },
  methods: {
    getCurrentSchool() {
      // Check cookie first (set by index.html script)
      const cookieMatch = document.cookie.match(/ecommerce_school=([^;]+)/);
      if (cookieMatch) {
        return cookieMatch[1];
      }

      // Fallback to checking URL path
      const pathSegments = window.location.pathname.toLowerCase().split('/').filter(s => s.length > 0);
      const schoolMap = ['westmoreland', 'windermere', 'test', 'coastalAB'];
      if (pathSegments.length > 0 && schoolMap.includes(pathSegments[0])) {
        return pathSegments[0];
      }

      // Default
      return 'westmoreland';
    },
    async checkSchoolChange() {
      const currentSchool = this.getCurrentSchool();
      const lastSchool = localStorage.getItem('last_school');

      if (lastSchool && lastSchool !== currentSchool) {
        logger.debug(`🏫 School changed from "${lastSchool}" to "${currentSchool}", clearing cart`);

        // Clear the cart when switching schools
        await this.$store.dispatch('clearCartFromStorage');

        // Show notification
        if (this.$toast) {
          this.$toast.info('Your cart has been cleared due to school change', {
            duration: 4000,
            position: 'top-right'
          });
        }
      }

      // Save current school for future comparison
      localStorage.setItem('last_school', currentSchool);
    },
    async restoreCartFromStorage() {
      try {
        // Only restore cart if current cart is empty
        if (this.$store.state.cart.items.length === 0) {
          // Use enhanced cart recovery for payment return scenarios
          let restored = false;

          // Check if we're returning from a payment page
          const isPaymentReturn = window.location.pathname.includes('payment') ||
                                window.location.search.includes('transactionReference') ||
                                window.location.search.includes('orderReference');

          if (isPaymentReturn) {
            logger.debug('🔄 Payment return detected, using enhanced cart recovery');
            restored = await this.$store.dispatch('recoverCartFromPaymentReturn');
          } else {
            // Standard cart restoration
            restored = await this.$store.dispatch('restoreCartFromStorage');
          }

          if (restored) {
            logger.debug('🛒 Cart successfully restored');

            // Show a subtle notification that cart was restored
            if (this.$toast) {
              this.$toast.info('Your cart has been restored', '', {
                duration: 3000,
                position: 'bottom-right'
              });
            }
          }
        }
      } catch (error) {
        logger.error('💥 Failed to restore cart on app initialization:', error);
      }
    },
    closeLoginModal() {
      this.showLoginModal = false
    },
    openCart() {
      // Emit event to header to open cart
      this.$root.$emit('open-cart')
      // Clear all toasts when cart is opened
      this.closeToasts()
    },
    closeToasts() {
      this.$refs.toastManager.clearAllToasts()
    },
    debugStore() {
      logger.debug('🔧 Store debug:');
      logger.debug('  - configLoading:', this.configLoading);
      logger.debug('  - configLoaded:', this.configLoaded);
      logger.debug('  - configError:', this.configError);
      logger.debug('  - store state:', this.$store.state.config);
      logger.debug('  - features:', this.$store.state.config && this.$store.state.config.features);
    }
  },
  async mounted() {
    // Listen for cart dock changes
    this.$root.$on('cart-dock-changed', (isDocked) => {
      this.isCartDocked = isDocked
      logger.debug('🛒 Cart dock state changed:', isDocked)
    })

    let configResult = null;

    try {
      // Load feature flags first
      logger.debug('🔧 Loading feature flags on app startup...');
      logger.debug('🔧 Initial state - configLoading:', this.configLoading, 'configLoaded:', this.configLoaded);

      configResult = await this.$store.dispatch('config/loadConfiguration');

      logger.debug('🔧 After API call - configLoading:', this.configLoading, 'configLoaded:', this.configLoaded);

      if (configResult.success) {
        logger.debug('✅ Feature flags loaded successfully');
      } else {
        logger.debug('⚠️ Feature flags failed to load, using defaults:', configResult.error);
      }

      // Final state check
      logger.debug('🔧 Final state - configLoading:', this.configLoading, 'configLoaded:', this.configLoaded);
    } catch (error) {
      logger.warn('⚠️ Failed to load feature flags (non-critical, app will continue):', error);
    }

    // Restore auth session persisted before WorldPay redirect
    try {
      const saved = sessionStorage.getItem('sv_auth')
      if (saved) {
        const { loggedIn, loggedInUser, currentWallet } = JSON.parse(saved)
        if (loggedIn) {
          this.$store.commit('setLoggedIn', true)
          this.$store.commit('setUser', loggedInUser)
          this.$store.commit('updateField', { path: 'currentWallet', value: currentWallet })
          // Restore HTTP request credentials so post-redirect API calls use user auth
          updateApiCredentials(loggedInUser)
          logger.debug('✅ Auth session restored from sessionStorage')
        }
      }
    } catch (_) { /* sessionStorage unavailable or corrupt — stay as guest */ }

    // Set app as ready regardless of feature flag success/failure
    this.appReady = true;
    logger.debug('✅ App is ready to display');

    // Wait for next tick to ensure ToastManager is rendered before accessing $refs
    await this.$nextTick();

    // Make toast manager globally accessible
    this.$root.$toastManager = this.$refs.toastManager

    // Initialize toast service
    if (this.$refs.toastManager) {
      this.$toast.init(this.$refs.toastManager)
      logger.debug('✅ Toast manager initialized successfully');
    } else {
      logger.warn('⚠️ Toast manager ref not available');
    }

    // Show configuration warning AFTER toast is initialized
    // Only show if config failed AND we're not in a native app context
    // (native apps on localhost hit this on first load before config.js is resolved)
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform())
    if (!configResult || !configResult.success) {
      if (!isNative) {
        this.$toast.warning('Some features may be limited due to configuration issues', '', {
          duration: 5000,
          position: 'top-right'
        });
      } else {
        logger.warn('⚠️ Config load failed in native context — suppressing toast, app will retry on next load');
      }
    }

    // Listen for cart toast clearing events
    this.$root.$on('clear-cart-toasts', this.closeToasts)

    // Check if school has changed and clear cart if needed
    await this.checkSchoolChange()

    // Restore cart from localStorage on app initialization
    await this.restoreCartFromStorage()
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('clear-cart-toasts', this.closeToasts)
    this.$root.$off('cart-dock-changed')
  }
}
</script>

<style>
#app {
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Legacy styles for existing components */
.outerDiv {
  @apply min-h-screen;
}

.mainDiv {
  @apply container mx-auto px-4 py-8;
}

.panel {
  @apply bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto;
}

/* Gradient shadow below sticky header for depth effect */
.header-with-gradient {
  position: relative;
}

.header-with-gradient::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  transform: translateY(100%);
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.04) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
  z-index: -1;
}
</style>
